import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Building2,
  LogOut,
  Trash2,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  CalendarDays,
  Download,
  Ban,
  Pencil,
  FileBarChart,
  Search,
  RefreshCw,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AdminCalendar from "./AdminCalendar";
import AdminBlocks from "./AdminBlocks";
import DailyReport from "./DailyReport";
import RangeReport from "./RangeReport";
import EditBookingDialog from "../components/EditBookingDialog";
import { formatSlot, formatSlots } from "@/lib/formatSlot";

import { API } from "@/lib/apiBase";
import { clearAdminToken, isAdminAuthenticated } from "@/lib/adminAuth";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingFilter, setBookingFilter] = useState("all");
  const [bookingDateFilter, setBookingDateFilter] = useState("");
  const [bookingCourtFilter, setBookingCourtFilter] = useState("all");
  const [bookingSearch, setBookingSearch] = useState("");
  const [bookingPage, setBookingPage] = useState(1);
  const BOOKINGS_PER_PAGE = 25;
  const [courtDetail, setCourtDetail] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const statsResponse = await axios.get(`${API}/admin/stats`);
      setStats(statsResponse.data);
      
      if (activeTab === "bookings" || activeTab === "dashboard") {
        const bookingsResponse = await axios.get(`${API}/admin/bookings`);
        setBookings(bookingsResponse.data);
      }
      
      if (activeTab === "courts" || activeTab === "dashboard" || activeTab === "bookings") {
        const courtsResponse = await axios.get(`${API}/courts`);
        setCourts(courtsResponse.data);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    // RequireAdmin guards the route, but double-check here in case the token
    // expired between renders.
    if (!isAdminAuthenticated()) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [activeTab, fetchData, navigate]);

  const handleLogout = () => {
    clearAdminToken();
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [opPrefilling, setOpPrefilling] = useState(false);

  // Current customer-facing booking-window utilization — drives the
  // "Open next 2 weeks" button and its low-availability pulse state.
  const [windowStatus, setWindowStatus] = useState(null);
  const [extendingWindow, setExtendingWindow] = useState(false);

  const fetchWindowStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/booking-window`);
      setWindowStatus(data);
    } catch {
      /* non-blocking — button just won't render until this succeeds */
    }
  }, []);

  useEffect(() => {
    if (!isAdminAuthenticated()) return;
    fetchWindowStatus();
  }, [fetchWindowStatus]);

  const handleExtendWindow = async () => {
    if (!window.confirm(
      "Open the customer-facing booking window by +14 more days? " +
      "This is repeatable — each click extends by another 2 weeks."
    )) return;
    setExtendingWindow(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/settings/booking-window/extend`,
        { additional_days: 14 }
      );
      toast.success(
        `Booking window now open for ${data.window_days} days (+${data.added} added).`
      );
      await fetchWindowStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extend window");
    } finally {
      setExtendingWindow(false);
    }
  };

  const handleGcalBackfill = async () => {
    if (!window.confirm(
      "Push all confirmed bookings and manual blocks missing from Google Calendar? " +
      "This is idempotent — entries already synced will be skipped."
    )) return;
    setGcalSyncing(true);
    try {
      const { data: status } = await axios.get(`${API}/admin/gcal/status`);
      if (!status.service_ready) {
        toast.error("Google Calendar service not ready. Check backend env vars.");
        return;
      }
      const { data } = await axios.post(`${API}/admin/gcal/backfill`);
      const b = data.bookings;
      const m = data.manual_blocks;
      const msg = `Bookings synced: ${b.synced}/${b.found} · Manual block groups synced: ${m.groups_synced}/${m.groups_found} (${m.slots_synced} slots)`;
      if ((b.failures?.length || 0) + (m.failures?.length || 0) > 0) {
        toast.warning(msg + " — some failures, see console");
        console.warn("GCal backfill failures:", { bookings: b.failures, blocks: m.failures });
      } else if (b.found === 0 && m.slots_found === 0) {
        toast.success("Already in sync — nothing to push.");
      } else {
        toast.success(msg);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setGcalSyncing(false);
    }
  };

  // Prefill recurring OP (Open Play) blocks on Courts 1-3 for every date in
  // the current booking window. The backend is idempotent — safe to click
  // multiple times. Existing customer bookings are always preserved.
  const handlePrefillOP = async () => {
    if (opPrefilling) return;
    if (!window.confirm(
      "Prefill OP blocks on Courts 1–3 for the entire booking window?\n\n"
      + "• Mon–Sun 8 AM – 12 PM\n"
      + "• Mon–Fri 1 PM – 4 PM\n"
      + "• Mon–Sun 5 PM – 1 AM\n\n"
      + "Existing bookings and manual blocks are skipped. Safe to re-run."
    )) return;
    setOpPrefilling(true);
    try {
      const { data } = await axios.post(`${API}/admin/blocks/prefill-op`);
      const parts = [
        `Inserted ${data.inserted}`,
        `${data.skipped_existing} already blocked`,
        `${data.skipped_booked} booked (preserved)`,
      ];
      if (data.inserted === 0 && data.skipped_existing > 0) {
        toast.success(`OP schedule already up to date · ${parts.join(" · ")}`);
      } else {
        toast.success(`OP prefill complete · ${parts.join(" · ")}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "OP prefill failed");
    } finally {
      setOpPrefilling(false);
    }
  };

  // Lazy-load a booking's receipt image on click. The bookings list no
  // longer ships the Base64 image (it can be several MB); we fetch just
  // the requested one on-demand and open it in a new tab. `window.open`
  // is called with the data URL directly — passing `noopener` returns
  // `null` in Chromium, and `document.write` fails on data-URL blob writes
  // in Safari. Navigating the popup straight to the data URL lets the
  // browser render it natively in its image viewer.
  const openReceiptImage = async (bookingId) => {
    try {
      const res = await axios.get(
        `${API}/admin/bookings/${bookingId}/receipt-image`
      );
      const dataUrl = res.data?.receipt_image;
      if (!dataUrl) {
        toast.error("No receipt image on file");
        return;
      }
      const w = window.open(dataUrl, "_blank");
      if (!w) {
        toast.error("Please allow pop-ups to view the receipt");
      }
    } catch (err) {
      console.error("openReceiptImage failed", err);
      toast.error("Failed to load receipt");
    }
  };




  const handleConfirmBooking = async (bookingId) => {
    // Find awaiting competitors for this booking (only those not yet rejected/confirmed)
    const target = bookings.find((b) => b.id === bookingId);
    let rejectCompetitors = false;
    if (target) {
      const ownSlots = new Set(target.time_slots || []);
      const awaitingCompetitors = bookings.filter(
        (b) =>
          b.id !== target.id &&
          b.court_id === target.court_id &&
          b.booking_date === target.booking_date &&
          b.status !== "rejected" &&
          b.status !== "cancelled" &&
          b.status !== "confirmed" &&
          (b.time_slots || []).some((s) => ownSlots.has(s))
      );
      if (awaitingCompetitors.length > 0) {
        const n = awaitingCompetitors.length;
        const ok = window.confirm(
          `Confirm this booking?\n\n${n} other customer${n > 1 ? "s" : ""} submitted for the same slot. Click OK to confirm this booking AND automatically reject the other ${n} (they will receive a rejection email). Click Cancel to abort.`
        );
        if (!ok) return;
        rejectCompetitors = true;
      }
    }
    try {
      const url = `${API}/admin/bookings/${bookingId}/confirm${
        rejectCompetitors ? "?reject_competitors=true" : ""
      }`;
      const res = await axios.post(url);
      const rejected = res?.data?.competitors_rejected || 0;
      const emailOk = res?.data?.customer_email_sent;
      const baseMsg =
        rejected > 0
          ? `Booking confirmed. ${rejected} competitor${rejected > 1 ? "s" : ""} rejected & notified.`
          : "Booking confirmed";
      // Surface a yellow warning when the customer email failed so the
      // admin knows to either fix SMTP or hit "Resend Email" manually.
      if (emailOk === false) {
        toast.warning(`${baseMsg}, but customer email failed to send. Use "Resend Email" once SMTP is fixed.`);
      } else {
        toast.success(baseMsg);
      }
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to confirm booking";
      toast.error(msg);
    }
  };

  const handleRejectBooking = async (bookingId) => {
    if (!window.confirm("Reject this booking?")) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/reject`);
      toast.success("Booking rejected");
      fetchData();
    } catch {
      toast.error("Failed to reject booking");
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Cancel this booking? The customer will be notified by email and any Google Calendar event will be removed.")) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to cancel booking";
      toast.error(msg);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Permanently DELETE this booking? This cannot be undone. The Google Calendar event (if any) will also be removed.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success("Booking deleted");
      fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to delete booking";
      toast.error(msg);
    }
  };

  const handleResendConfirmation = async (bookingId) => {
    try {
      const res = await axios.post(
        `${API}/admin/bookings/${bookingId}/resend-confirmation`
      );
      const sent = res?.data?.sent;
      const to = res?.data?.to;
      if (sent) {
        toast.success(`Confirmation email re-sent to ${to}`);
      } else {
        toast.error(
          `Email send failed${to ? ` (${to})` : ""}. Check production SMTP credentials.`
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Failed to resend confirmation email";
      toast.error(msg);
    }
  };

  const cleanupCount = bookings.filter(
    (b) => b.status === "rejected" || b.status === "cancelled"
  ).length;

  const bookingFilters = (() => {
    // Compute today's date (browser local) once per render so the "Upcoming"
    // matcher is a simple string compare against booking_date (YYYY-MM-DD).
    const now = new Date();
    const todayIso =
      `${now.getFullYear()}-` +
      `${String(now.getMonth() + 1).padStart(2, "0")}-` +
      `${String(now.getDate()).padStart(2, "0")}`;
    return [
      { key: "all", label: "All", match: () => true },
      {
        key: "upcoming",
        label: "Upcoming",
        // Current day + every future date. Cancelled/rejected bookings are
        // intentionally excluded — "upcoming" means "you need to be aware
        // of this one", not "you need to clean it up".
        match: (b) =>
          (b.booking_date || "") >= todayIso &&
          b.status !== "cancelled" &&
          b.status !== "rejected",
      },
      { key: "awaiting_confirmation", label: "Awaiting", match: (b) => b.status === "awaiting_confirmation" },
      { key: "pending", label: "Pending", match: (b) => b.status === "pending" },
      { key: "confirmed", label: "Confirmed", match: (b) => b.status === "confirmed" },
      { key: "cancelled", label: "Cancelled", match: (b) => b.status === "cancelled" },
      { key: "rejected", label: "Rejected", match: (b) => b.status === "rejected" },
    ];
  })();

  // Compose all four axes: status pill → date → court → free-text search.
  // Then priority-sort so bookings awaiting admin action surface at the top,
  // followed by the most recently created bookings.
  const statusMatcher =
    bookingFilters.find((f) => f.key === bookingFilter)?.match || (() => true);
  const searchTerm = bookingSearch.trim().toLowerCase();
  const filteredBookings = bookings
    .filter(statusMatcher)
    .filter((b) => !bookingDateFilter || b.booking_date === bookingDateFilter)
    .filter(
      (b) => bookingCourtFilter === "all" || b.court_id === bookingCourtFilter
    )
    .filter((b) => {
      if (!searchTerm) return true;
      // Search across the fields an admin would naturally type — name, email,
      // phone, court name, booking id, booking_group_id, status, payment_method.
      const haystack = [
        b.customer_name,
        b.customer_email,
        b.customer_phone,
        b.court_name,
        b.linked_court_name,
        b.id,
        b.booking_group_id,
        b.status,
        b.payment_method,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchTerm);
    })
    .slice() // don't mutate the source array
    .sort((a, b) => {
      // 1) Awaiting confirmation always first
      const aWait = a.status === "awaiting_confirmation" ? 0 : 1;
      const bWait = b.status === "awaiting_confirmation" ? 0 : 1;
      if (aWait !== bWait) return aWait - bWait;
      // 2) Within the same priority bucket, latest created first
      const aTs = a.created_at || "";
      const bTs = b.created_at || "";
      if (aTs !== bTs) return aTs < bTs ? 1 : -1;
      return 0;
    });

  // Pagination — derive current page slice from the filtered/sorted array.
  // `bookingPage` is capped at the last-available page so filter changes
  // that shrink the total row count never leave the user on an empty page.
  const totalPages = Math.max(
    1,
    Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE)
  );
  const safePage = Math.min(Math.max(1, bookingPage), totalPages);
  const pagedBookings = filteredBookings.slice(
    (safePage - 1) * BOOKINGS_PER_PAGE,
    safePage * BOOKINGS_PER_PAGE
  );

  // Reset to page 1 whenever the filters change so the visible slice stays
  // in sync with the new result set. We compare simple primitive keys — no
  // deep equality needed since these are all strings.
  useEffect(() => {
    setBookingPage(1);
  }, [bookingFilter, bookingDateFilter, bookingCourtFilter, bookingSearch]);

  const exportCsv = () => {
    if (!filteredBookings.length) {
      toast.error("Nothing to export");
      return;
    }
    const headers = [
      "Booking ID",
      "Customer",
      "Email",
      "Phone",
      "Court",
      "Date",
      "Time Slots",
      "Amount (PHP)",
      "Payment Method",
      "Payment Status",
      "Booking Status",
      "Created At",
    ];
    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const str = String(val);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const rows = filteredBookings.map((b) => [
      b.id,
      b.customer_name,
      b.customer_email,
      b.customer_phone,
      b.court_name,
      b.booking_date,
      (b.time_slots ? formatSlots(b.time_slots).join(" | ") : ""),
      b.total_amount,
      b.payment_method || "",
      b.payment_status || "",
      b.status || "",
      b.created_at || "",
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map(escape).join(","))
      .join("\r\n");
    // Prefix BOM so Excel opens PHP peso and UTF-8 correctly
    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `assenda-bookings-${bookingFilter}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} booking(s)`);
  };

  const handleCleanup = async () => {
    if (cleanupCount === 0) {
      toast.error("No rejected or cancelled bookings to clear");
      return;
    }
    if (
      !window.confirm(
        `Delete ${cleanupCount} rejected / cancelled booking(s) permanently? This cannot be undone.`
      )
    )
      return;
    try {
      const res = await axios.delete(`${API}/admin/bookings/bulk/cleanup`);
      toast.success(`Deleted ${res.data.deleted} booking(s)`);
      fetchData();
    } catch {
      toast.error("Cleanup failed");
    }
  };

  // Count other bookings competing for the same slot(s) on the same court/date.
  // A booking "competes" if it shares ≥1 time slot and is not rejected/cancelled
  // and is not the booking itself.
  const getCompetingBookings = (booking) => {
    const ownSlots = new Set(booking.time_slots || []);
    return bookings.filter(
      (b) =>
        b.id !== booking.id &&
        b.court_id === booking.court_id &&
        b.booking_date === booking.booking_date &&
        b.status !== "rejected" &&
        b.status !== "cancelled" &&
        (b.time_slots || []).some((s) => ownSlots.has(s))
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-black tracking-tighter text-white">
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain cursor-pointer"
              data-testid="brand-logo"
              onClick={() => navigate("/")}
            />
            <span className="text-xs ml-3 text-[#a1a1aa] font-normal">Admin</span>
          </h1>
          <div className="flex items-center gap-4">
            <button
              data-testid="prefill-op-button"
              onClick={handlePrefillOP}
              disabled={opPrefilling}
              title="Prefill recurring OP (Open Play) blocks for Courts 1–3 across the current booking window. Existing bookings are preserved."
              className="text-sm font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Sparkles size={16} className={opPrefilling ? "animate-pulse" : ""} />
              {opPrefilling ? "Prefilling…" : "Prefill OP"}
            </button>
            <button
              data-testid="gcal-sync-button"
              onClick={handleGcalBackfill}
              disabled={gcalSyncing}
              title="Push any confirmed bookings or manual blocks that aren't yet in Google Calendar"
              className="text-sm font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={16} className={gcalSyncing ? "animate-spin" : ""} />
              {gcalSyncing ? "Syncing…" : "Sync GCal"}
            </button>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="text-sm font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors flex items-center gap-2"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar & Content */}
      <div className="pt-20 flex">
        {/* Sidebar */}
        <aside className="w-64 h-screen sticky top-20 border-r border-white/10 p-6">
          <nav className="space-y-2">
            <button
              data-testid="nav-dashboard"
              onClick={() => setActiveTab("dashboard")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "dashboard"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <LayoutDashboard size={20} />
              Dashboard
            </button>
            <button
              data-testid="nav-bookings"
              onClick={() => setActiveTab("bookings")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "bookings"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <Calendar size={20} />
              Bookings
            </button>
            <button
              data-testid="nav-calendar"
              onClick={() => setActiveTab("calendar")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "calendar"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <CalendarDays size={20} />
              Calendar
            </button>
            <button
              data-testid="nav-daily-report"
              onClick={() => setActiveTab("daily-report")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "daily-report"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <FileBarChart size={20} />
              Daily Report
            </button>
            <button
              data-testid="nav-range-report"
              onClick={() => setActiveTab("range-report")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "range-report"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <FileBarChart size={20} />
              Sales Report
            </button>
            <button
              data-testid="nav-blocks"
              onClick={() => setActiveTab("blocks")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "blocks"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <Ban size={20} />
              Blocks
            </button>
            <button
              data-testid="nav-courts"
              onClick={() => setActiveTab("courts")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                activeTab === "courts"
                  ? "bg-[#8cc63f] text-black"
                  : "text-[#a1a1aa] hover:text-white hover:bg-[#121212]"
              }`}
            >
              <Building2 size={20} />
              Courts
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Dashboard Tab */}
              {activeTab === "dashboard" && (
                <div data-testid="dashboard-content">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-8">
                    <h2 className="text-3xl font-bold">Dashboard Overview</h2>
                    {windowStatus && (
                      <button
                        type="button"
                        data-testid="open-next-2-weeks-btn"
                        onClick={handleExtendWindow}
                        disabled={extendingWindow}
                        title={`Booking window currently open for ${windowStatus.window_days} days. ${Math.round(windowStatus.pct_booked * 100)}% booked.`}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-sm border transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                          windowStatus.low_availability
                            ? "border-[#8cc63f] bg-[#8cc63f] text-black hover:bg-[#9ed15c] shadow-[0_0_0_0_rgba(140,198,63,0.6)] animate-[segmented-active-ring_1.4s_ease-out_infinite]"
                            : "border-white/20 bg-white/[0.03] text-[#a1a1aa] hover:border-[#8cc63f] hover:text-[#8cc63f]"
                        }`}
                      >
                        <CalendarDays size={14} />
                        {extendingWindow ? "Extending…" : "Open next 2 weeks"}
                        <span
                          data-testid="booking-window-days"
                          className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${
                            windowStatus.low_availability
                              ? "bg-black/20 text-black"
                              : "bg-white/10 text-white"
                          }`}
                        >
                          {windowStatus.window_days}d · {Math.round(windowStatus.pct_booked * 100)}% booked
                        </span>
                      </button>
                    )}
                  </div>
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    <div className="card-surface p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-sm bg-[#8cc63f] flex items-center justify-center">
                          <Calendar className="text-black" size={20} />
                        </div>
                        <span className="label-text">Total Bookings</span>
                      </div>
                      <p className="text-3xl font-black" data-testid="total-bookings">
                        {stats.total_bookings || 0}
                      </p>
                    </div>
                    
                    <div className="card-surface p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-sm bg-[#8cc63f] flex items-center justify-center">
                          <CheckCircle className="text-black" size={20} />
                        </div>
                        <span className="label-text">Confirmed</span>
                      </div>
                      <p className="text-3xl font-black" data-testid="confirmed-bookings">
                        {stats.confirmed_bookings || 0}
                      </p>
                    </div>
                    
                    <div className="card-surface p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-sm bg-[#8cc63f] flex items-center justify-center">
                          <Building2 className="text-black" size={20} />
                        </div>
                        <span className="label-text">Active Courts</span>
                      </div>
                      <p className="text-3xl font-black" data-testid="total-courts">
                        {stats.total_courts || 0}
                      </p>
                    </div>
                    
                    <div className="card-surface p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-sm bg-[#8cc63f] flex items-center justify-center">
                          <DollarSign className="text-black" size={20} />
                        </div>
                        <span className="label-text">Total Revenue</span>
                      </div>
                      <p className="text-3xl font-black" data-testid="total-revenue">
                        ₱{stats.total_revenue?.toLocaleString() || "0"}
                      </p>
                    </div>
                  </div>

                  {/* Recent Bookings */}
                  <div className="card-surface p-6">
                    <h3 className="text-xl font-bold mb-4">Recent Bookings</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 px-2 label-text">Customer</th>
                            <th className="text-left py-3 px-2 label-text">Court</th>
                            <th className="text-left py-3 px-2 label-text">Date</th>
                            <th className="text-left py-3 px-2 label-text">Time</th>
                            <th className="text-left py-3 px-2 label-text">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bookings.slice(0, 5).map((booking) => (
                            <tr key={booking.id} className="border-b border-white/10">
                              <td className="py-3 px-2">{booking.customer_name}</td>
                              <td className="py-3 px-2">{booking.court_name}</td>
                              <td className="py-3 px-2">{booking.booking_date}</td>
                              <td className="py-3 px-2 text-xs">
                                {booking.time_slots?.join(", ") || booking.time_slot}
                              </td>
                              <td className="py-3 px-2">
                                <span
                                  className={`px-2 py-1 rounded-sm text-xs font-semibold ${
                                    booking.status === "confirmed"
                                      ? "bg-[#8cc63f] text-black"
                                      : "bg-[#a1a1aa] text-black"
                                  }`}
                                >
                                  {booking.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === "bookings" && (
                <div data-testid="bookings-content">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                    <h2 className="text-3xl font-bold">All Bookings</h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        data-testid="export-csv-button"
                        onClick={exportCsv}
                        disabled={filteredBookings.length === 0}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold bg-transparent text-[#8cc63f] border border-[#8cc63f]/40 rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Download size={14} />
                        Export CSV
                        <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-white/10 text-white text-[10px] font-bold">
                          {filteredBookings.length}
                        </span>
                      </button>
                      <button
                        data-testid="cleanup-bookings-button"
                        onClick={handleCleanup}
                        disabled={cleanupCount === 0}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold bg-transparent text-[#a1a1aa] border border-white/15 rounded-sm hover:border-red-400 hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                        Clear rejected &amp; cancelled
                        {cleanupCount > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-red-500/20 text-red-300 text-[10px] font-bold">
                            {cleanupCount}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="card-surface p-6">
                    <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-white/10">
                      {bookingFilters.map((f) => {
                        const count = bookings.filter(f.match).length;
                        const active = bookingFilter === f.key;
                        return (
                          <button
                            key={f.key}
                            data-testid={`filter-${f.key}`}
                            onClick={() => setBookingFilter(f.key)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors flex items-center gap-2 ${
                              active
                                ? "bg-[#8cc63f] text-black border-[#8cc63f]"
                                : "bg-transparent text-[#a1a1aa] border-white/10 hover:border-[#8cc63f] hover:text-white"
                            }`}
                          >
                            {f.label}
                            <span
                              className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                                active ? "bg-black/20 text-black" : "bg-white/10 text-white"
                              }`}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Secondary filters: search + date + court. Independent
                        of the status pills above and combined via AND. */}
                    <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-white/10">
                      <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                        <label className="label-text text-[10px]" htmlFor="booking-search-input">
                          Search
                        </label>
                        <div className="relative">
                          <Search
                            size={13}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#a1a1aa] pointer-events-none"
                          />
                          <input
                            id="booking-search-input"
                            data-testid="booking-search-input"
                            type="search"
                            value={bookingSearch}
                            onChange={(e) => setBookingSearch(e.target.value)}
                            placeholder="Name, email, phone, court, booking ID…"
                            className="w-full bg-[#0a0a0a] text-white text-xs font-semibold border border-white/15 rounded-sm pl-7 pr-2 py-1.5 hover:border-[#8cc63f]/60 focus:border-[#8cc63f] focus:outline-none placeholder:text-[#525252] placeholder:font-normal"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="label-text text-[10px]" htmlFor="booking-date-filter">
                          Booking date
                        </label>
                        <input
                          id="booking-date-filter"
                          data-testid="booking-date-filter"
                          type="date"
                          value={bookingDateFilter}
                          onChange={(e) => setBookingDateFilter(e.target.value)}
                          className="bg-[#0a0a0a] text-white text-xs font-semibold border border-white/15 rounded-sm px-2 py-1.5 hover:border-[#8cc63f]/60 focus:border-[#8cc63f] focus:outline-none"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="label-text text-[10px]" htmlFor="booking-court-filter">
                          Court
                        </label>
                        <select
                          id="booking-court-filter"
                          data-testid="booking-court-filter"
                          value={bookingCourtFilter}
                          onChange={(e) => setBookingCourtFilter(e.target.value)}
                          className="bg-[#0a0a0a] text-white text-xs font-semibold border border-white/15 rounded-sm px-2 py-1.5 hover:border-[#8cc63f]/60 focus:border-[#8cc63f] focus:outline-none min-w-[140px]"
                        >
                          <option value="all">All courts</option>
                          {courts.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(bookingDateFilter || bookingCourtFilter !== "all" || bookingSearch) && (
                        <button
                          type="button"
                          data-testid="clear-booking-filters"
                          onClick={() => {
                            setBookingDateFilter("");
                            setBookingCourtFilter("all");
                            setBookingSearch("");
                          }}
                          className="px-3 py-1.5 text-xs font-bold text-[#a1a1aa] border border-white/15 rounded-sm hover:border-red-400 hover:text-red-400 transition-colors"
                        >
                          Clear filters
                        </button>
                      )}
                      <p className="ml-auto text-xs text-[#a1a1aa] self-center">
                        Showing <span className="text-white font-bold">{filteredBookings.length}</span> of {bookings.length}
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      {filteredBookings.length === 0 ? (
                        <p className="text-center text-[#a1a1aa] py-12 text-sm">
                          No bookings match this filter.
                        </p>
                      ) : (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 px-2 label-text">Customer</th>
                            <th className="text-left py-3 px-2 label-text">Email</th>
                            <th className="text-left py-3 px-2 label-text">Phone</th>
                            <th className="text-left py-3 px-2 label-text">Court</th>
                            <th className="text-left py-3 px-2 label-text">Date</th>
                            <th className="text-left py-3 px-2 label-text">Time</th>
                            <th className="text-left py-3 px-2 label-text">Amount</th>
                            <th className="text-left py-3 px-2 label-text">Method</th>
                            <th className="text-left py-3 px-2 label-text">Receipt</th>
                            <th className="text-left py-3 px-2 label-text">Status</th>
                            <th className="text-left py-3 px-2 label-text">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedBookings.map((booking) => {
                            const competing = getCompetingBookings(booking);
                            const competingCount = competing.length;
                            return (
                            <tr key={booking.id} className="border-b border-white/10">
                              <td className="py-3 px-2">{booking.customer_name}</td>
                              <td className="py-3 px-2 text-[#a1a1aa] text-sm">{booking.customer_email}</td>
                              <td className="py-3 px-2 text-[#a1a1aa] text-sm">{booking.customer_phone || "—"}</td>
                              <td className="py-3 px-2">{booking.court_name}</td>
                              <td className="py-3 px-2">{booking.booking_date}</td>
                              <td className="py-3 px-2 text-xs">
                                {booking.time_slots
                                  ? formatSlots(booking.time_slots).join(", ")
                                  : booking.time_slot ? formatSlot(booking.time_slot) : ""}
                              </td>
                              <td className="py-3 px-2 font-semibold">₱{booking.total_amount.toLocaleString()}</td>
                              <td className="py-3 px-2 uppercase text-xs">{booking.payment_method || "—"}</td>
                              <td className="py-3 px-2">
                                {booking.has_receipt ? (
                                  <button
                                    type="button"
                                    data-testid={`view-receipt-${booking.id}`}
                                    onClick={() => openReceiptImage(booking.id)}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-sm border border-[#8cc63f]/40 bg-[#8cc63f]/10 text-[#8cc63f] hover:bg-[#8cc63f]/20 hover:border-[#8cc63f] transition-colors uppercase"
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span className="text-[#a1a1aa] text-sm">—</span>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`px-2 py-1 rounded-sm text-xs font-semibold whitespace-nowrap ${
                                      booking.payment_status === "paid"
                                        ? "bg-[#8cc63f] text-black"
                                        : booking.payment_status === "awaiting_confirmation"
                                        ? "bg-yellow-400 text-black"
                                        : booking.payment_status === "rejected"
                                        ? "bg-red-500 text-white"
                                        : "bg-[#a1a1aa] text-black"
                                    }`}
                                  >
                                    {booking.payment_status}
                                  </span>
                                  {booking.status !== "confirmed" &&
                                    booking.status !== "rejected" &&
                                    competingCount > 0 && (
                                      <span
                                        data-testid={`conflict-badge-${booking.id}`}
                                        title={`${competingCount} other customer${competingCount > 1 ? "s" : ""} also submitted for this slot`}
                                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold bg-orange-500/20 text-orange-300 border border-orange-500/40 whitespace-nowrap"
                                      >
                                        <AlertTriangle size={12} />
                                        {competingCount} conflict{competingCount > 1 ? "s" : ""}
                                      </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    data-testid={`edit-booking-${booking.id}`}
                                    title="Edit booking details"
                                    onClick={() => setEditingBooking(booking)}
                                    className="p-1 text-[#a1a1aa] hover:text-[#8cc63f] transition-colors"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  {booking.payment_status === "awaiting_confirmation" ? (
                                    <>
                                      <button
                                        data-testid={`confirm-booking-${booking.id}`}
                                        onClick={() => handleConfirmBooking(booking.id)}
                                        className="px-3 py-1 text-xs bg-[#8cc63f] text-black font-semibold rounded-sm hover:bg-[#9ed15c] transition-colors"
                                      >
                                        Confirm
                                      </button>
                                      <button
                                        data-testid={`reject-booking-${booking.id}`}
                                        onClick={() => handleRejectBooking(booking.id)}
                                        className="px-3 py-1 text-xs bg-transparent border border-red-500 text-red-500 font-semibold rounded-sm hover:bg-red-500 hover:text-white transition-colors"
                                      >
                                        Reject
                                      </button>
                                    </>
                                  ) : booking.status === "cancelled" ||
                                    booking.status === "rejected" ? null : (
                                    <>
                                      <button
                                        data-testid={`resend-confirmation-${booking.id}`}
                                        onClick={() => handleResendConfirmation(booking.id)}
                                        title="Resend confirmation email + calendar invite to the customer"
                                        className="px-3 py-1 text-xs bg-transparent border border-[#8cc63f] text-[#8cc63f] font-semibold rounded-sm hover:bg-[#8cc63f] hover:text-black transition-colors"
                                      >
                                        Resend Email
                                      </button>
                                      <button
                                        data-testid={`cancel-booking-${booking.id}`}
                                        onClick={() => handleCancelBooking(booking.id)}
                                        className="px-3 py-1 text-xs bg-transparent border border-red-500 text-red-500 font-semibold rounded-sm hover:bg-red-500 hover:text-white transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                  <button
                                    data-testid={`delete-booking-${booking.id}`}
                                    title="Permanently delete this booking"
                                    onClick={() => handleDeleteBooking(booking.id)}
                                    className="p-1 text-[#a1a1aa] hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      )}
                    </div>
                    {/* Pagination controls — hidden when everything fits on
                        one page. `data-testid` hooks let the test agent
                        drive the flow without brittle text selectors. */}
                    {filteredBookings.length > BOOKINGS_PER_PAGE && (
                      <div
                        data-testid="bookings-pagination"
                        className="flex items-center justify-between mt-4 flex-wrap gap-2"
                      >
                        <p className="text-xs text-[#a1a1aa]">
                          Page{" "}
                          <span className="text-white font-bold">
                            {safePage}
                          </span>{" "}
                          of {totalPages} ·{" "}
                          {(safePage - 1) * BOOKINGS_PER_PAGE + 1}-
                          {Math.min(
                            safePage * BOOKINGS_PER_PAGE,
                            filteredBookings.length
                          )}{" "}
                          of {filteredBookings.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            data-testid="bookings-prev-page"
                            onClick={() =>
                              setBookingPage((p) => Math.max(1, p - 1))
                            }
                            disabled={safePage <= 1}
                            className="px-3 py-1.5 text-xs font-bold border border-white/15 rounded-sm text-white hover:border-[#8cc63f] hover:text-[#8cc63f] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            ← Prev
                          </button>
                          <button
                            type="button"
                            data-testid="bookings-next-page"
                            onClick={() =>
                              setBookingPage((p) =>
                                Math.min(totalPages, p + 1)
                              )
                            }
                            disabled={safePage >= totalPages}
                            className="px-3 py-1.5 text-xs font-bold border border-white/15 rounded-sm text-white hover:border-[#8cc63f] hover:text-[#8cc63f] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Calendar Tab */}
              {activeTab === "calendar" && (
                <AdminCalendar refreshTick={activeTab === "calendar" ? 1 : 0} />
              )}

              {/* Daily Report Tab */}
              {activeTab === "daily-report" && <DailyReport />}

              {/* Sales / Duration Report Tab */}
              {activeTab === "range-report" && <RangeReport />}

              {/* Blocks Tab */}
              {activeTab === "blocks" && <AdminBlocks />}


              {/* Courts Tab */}
              {activeTab === "courts" && (
                <div data-testid="courts-content">
                  <div className="flex justify-between items-center mb-8">
                    <h2 className="text-3xl font-bold">Courts</h2>
                    <p className="text-xs text-[#a1a1aa]">Tap a court to see this month's activity</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {courts.map((court) => {
                      const now = new Date();
                      const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                      const monthBookings = bookings.filter(
                        (b) => b.court_id === court.id && (b.booking_date || "").startsWith(ym)
                      );
                      const confirmed = monthBookings.filter((b) => b.status === "confirmed");
                      const revenue = confirmed.reduce((s, b) => s + (b.total_amount || 0), 0);
                      return (
                        <button
                          key={court.id}
                          type="button"
                          data-testid={`court-card-${court.id}`}
                          onClick={() => setCourtDetail(court)}
                          className="card-surface p-6 text-left hover:border-[#8cc63f] transition-colors"
                        >
                          <h3 className="text-xl font-bold mb-2">{court.name}</h3>
                          <p className="text-sm text-[#a1a1aa] mb-1">{court.location}</p>
                          <p className="text-sm text-[#a1a1aa] mb-4">{court.description}</p>
                          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10 text-xs">
                            <div>
                              <p className="label-text mb-1">This month</p>
                              <p className="text-base font-bold text-white">
                                {confirmed.length}
                                <span className="text-[#a1a1aa] text-[11px] font-normal ml-1">
                                  confirmed
                                </span>
                              </p>
                            </div>
                            <div>
                              <p className="label-text mb-1">Revenue</p>
                              <p className="text-base font-bold text-[#8cc63f]">
                                ₱{revenue.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Court detail dialog */}
                  <Dialog open={!!courtDetail} onOpenChange={(o) => !o && setCourtDetail(null)}>
                    <DialogContent className="bg-[#121212] text-white max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-black tracking-tighter">
                          <span className="text-[#8cc63f]">{courtDetail?.name}</span>
                          <span className="text-xs ml-3 text-[#a1a1aa] font-normal">
                            {courtDetail?.location}
                          </span>
                        </DialogTitle>
                      </DialogHeader>
                      {courtDetail && (() => {
                        const now = new Date();
                        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                        const monthBookings = bookings
                          .filter(
                            (b) =>
                              b.court_id === courtDetail.id &&
                              (b.booking_date || "").startsWith(ym)
                          )
                          .sort((a, b) => (a.booking_date > b.booking_date ? 1 : -1));
                        const confirmed = monthBookings.filter((b) => b.status === "confirmed");
                        const revenue = confirmed.reduce((s, b) => s + (b.total_amount || 0), 0);
                        const monthLabel = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                        return (
                          <div className="space-y-5 mt-2">
                            <div className="grid grid-cols-3 gap-3">
                              <Stat label={monthLabel} value={monthBookings.length} sub="total" />
                              <Stat label="Confirmed" value={confirmed.length} sub="paid" />
                              <Stat
                                label="Revenue"
                                value={`₱${revenue.toLocaleString()}`}
                                green
                              />
                            </div>
                            <div className="max-h-[50vh] overflow-y-auto border border-white/10 rounded-sm">
                              {monthBookings.length === 0 ? (
                                <p className="text-center text-[#a1a1aa] text-sm py-8">
                                  No bookings on {courtDetail.name} this month.
                                </p>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 bg-[#121212]">
                                    <tr className="border-b border-white/10">
                                      <th className="text-left py-2 px-3 label-text">Date</th>
                                      <th className="text-left py-2 px-3 label-text">Time</th>
                                      <th className="text-left py-2 px-3 label-text">Customer</th>
                                      <th className="text-left py-2 px-3 label-text">Amount</th>
                                      <th className="text-left py-2 px-3 label-text">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {monthBookings.map((b) => (
                                      <tr key={b.id} className="border-b border-white/5">
                                        <td className="py-2 px-3">{b.booking_date}</td>
                                        <td className="py-2 px-3">
                                          {formatSlots(b.time_slots || []).join(", ")}
                                        </td>
                                        <td className="py-2 px-3">{b.customer_name}</td>
                                        <td className="py-2 px-3 font-semibold">
                                          ₱{(b.total_amount || 0).toLocaleString()}
                                        </td>
                                        <td className="py-2 px-3">
                                          <span
                                            className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${
                                              b.status === "confirmed"
                                                ? "bg-[#8cc63f] text-black"
                                                : b.status === "awaiting_confirmation"
                                                ? "bg-yellow-400 text-black"
                                                : b.status === "rejected" || b.status === "cancelled"
                                                ? "bg-red-500/20 text-red-300"
                                                : "bg-white/10 text-[#a1a1aa]"
                                            }`}
                                          >
                                            {b.status}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <EditBookingDialog
        booking={editingBooking}
        courts={courts}
        onClose={() => setEditingBooking(null)}
        onSaved={fetchData}
      />
    </div>
  );
};

const Stat = ({ label, value, sub, green }) => (
  <div className="card-surface p-3">
    <p className="label-text mb-1">{label}</p>
    <p className={`text-xl font-black ${green ? "text-[#8cc63f]" : "text-white"}`}>
      {value}
      {sub && <span className="text-[#a1a1aa] text-[10px] font-normal ml-1">{sub}</span>}
    </p>
  </div>
);

export default AdminDashboard;